import { LoadingButton } from '@mui/lab';
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';

import { Country, CreatePharmaceuticalCompanyDto, PharmaceuticalCompany } from 'shared/api';

const validationSchema = yup.object({
  fullName: yup.string().trim().required('Required'),
  abbreviatedName: yup.string().trim().required('Required'),
  russianName: yup.string().trim(),
  country: yup.string().trim().length(2, 'ISO-2 code (2 letters)').required('Required'),
  www: yup.string().trim().url('Must be a valid URL'),
  notes: yup.string().trim(),
});

type FormValues = CreatePharmaceuticalCompanyDto;

type Props = {
  title: string;
  isOpen: boolean;
  company?: PharmaceuticalCompany;
  countries: Country[];
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (data: FormValues) => void;
};

const emptyValues: FormValues = {
  fullName: '',
  abbreviatedName: '',
  russianName: '',
  country: '',
  www: '',
  notes: '',
};

export const PharmaCompanyDialog = ({ title, isOpen, company, countries, isLoading, onClose, onSubmit }: Props) => {
  const formik = useFormik<FormValues>({
    initialValues: emptyValues,
    validationSchema,
    onSubmit: (values) => {
      const trimOrUndefined = (v?: string) => {
        const trimmed = v?.trim();
        return trimmed && trimmed.length > 0 ? trimmed : undefined;
      };
      onSubmit({
        ...values,
        russianName: trimOrUndefined(values.russianName),
        www: trimOrUndefined(values.www),
        notes: trimOrUndefined(values.notes),
      });
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    if (company) {
      void formik.setValues({
        fullName: company.fullName ?? '',
        abbreviatedName: company.abbreviatedName ?? '',
        russianName: company.russianName ?? '',
        country: company.country ?? '',
        www: company.www ?? '',
        notes: company.notes ?? '',
      });
    } else {
      formik.resetForm({ values: emptyValues });
    }
  }, [company, isOpen]);

  const handleClose = () => {
    formik.resetForm({ values: emptyValues });
    onClose();
  };

  const selectedCountry = countries.find((c) => c.code === formik.values.country) ?? null;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              fullWidth
              name="fullName"
              label="Full name *"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.fullName && Boolean(formik.errors.fullName)}
              helperText={formik.touched.fullName && formik.errors.fullName}
            />

            <TextField
              fullWidth
              name="abbreviatedName"
              label="Abbreviated name *"
              value={formik.values.abbreviatedName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.abbreviatedName && Boolean(formik.errors.abbreviatedName)}
              helperText={formik.touched.abbreviatedName && formik.errors.abbreviatedName}
            />

            <TextField
              fullWidth
              name="russianName"
              label="Russian name"
              value={formik.values.russianName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <Autocomplete
              options={countries}
              value={selectedCountry}
              onChange={(_, value) => formik.setFieldValue('country', value?.code ?? '')}
              onBlur={() => formik.setFieldTouched('country', true)}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country *"
                  error={formik.touched.country && Boolean(formik.errors.country)}
                  helperText={formik.touched.country && formik.errors.country}
                />
              )}
            />

            <TextField
              fullWidth
              name="www"
              label="Website"
              placeholder="https://..."
              value={formik.values.www}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.www && Boolean(formik.errors.www)}
              helperText={formik.touched.www && formik.errors.www}
            />

            <TextField
              fullWidth
              name="notes"
              label="Notes"
              multiline
              rows={3}
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isLoading}>
            {company ? 'Update' : 'Create'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
